// components/LayerWebGLRaster.tsx
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { CANVAS_BASE_STYLE, collectClipElements, createWebGLRasterRenderer } from '#lib/studio-layer-webgl-raster';
import { STUDIO_VIEWPORT_TRANSFORM_EVENT, VIEWPORT_TRANSFORM_CONTAINER_SELECTOR } from '#lib/studio-viewport';

// Once per page: the same build failure would otherwise be reported by every XXL layer on each of its mounts.
let hasReportedShaderBuildFailure = false;

// A zero-size anchor at the bitmap origin: the canvas itself is created per mount, because a context released
// with WEBGL_lose_context stays lost on its canvas and StrictMode remounts onto the same DOM node.
const HOST_STYLE: React.CSSProperties = { position: 'absolute', left: 0, top: 0 };

interface LayerWebGLRasterProps {
    layerId: string;
    workspaceId: string;
    heatmapHue: number | null;
    onUnavailable: () => void;
}

// Mounted inside the bitmap-sized, display-scaled wrapper of a tiled layer, in place of its tiles.
const LayerWebGLRasterComponent: React.FC<LayerWebGLRasterProps> = ({ layerId, workspaceId, heatmapHue, onUnavailable }) => {
    const { kernel } = useKernel();
    const imageData = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.imageData);
    const contentVersion = useKernelStore((state) => state.inventory.StudioLayer.registry[layerId]?.contentVersion);
    // Moving or resizing the layer or its workspace changes what is on screen without rendering this component.
    const placement = useKernelStore((state) => {
        const rect = state.inventory.StudioLayer.registry[layerId]?.rect;
        const frame = state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect;
        return `${rect?.x}|${rect?.y}|${rect?.w}|${rect?.h}|${frame?.x}|${frame?.y}|${frame?.w}|${frame?.h}`;
    });
    const hostRef = useRef<HTMLDivElement | null>(null);
    const rendererRef = useRef<ReturnType<typeof createWebGLRasterRenderer>>(null);

    useLayoutEffect(() => {
        const host = hostRef.current;
        const bitmapBox = host?.parentElement;
        const viewportElement = host?.closest<HTMLElement>(VIEWPORT_TRANSFORM_CONTAINER_SELECTOR);
        const viewportBox = viewportElement?.parentElement;
        if (!host || !bitmapBox || !viewportElement || !viewportBox) {
            onUnavailable();
            return undefined;
        }
        const canvas = document.createElement('canvas');
        Object.assign(canvas.style, CANVAS_BASE_STYLE);
        host.appendChild(canvas);
        const renderer = createWebGLRasterRenderer(canvas, bitmapBox, viewportBox, collectClipElements(host, viewportElement), (log) => {
            if (hasReportedShaderBuildFailure) return;
            hasReportedShaderBuildFailure = true;
            console.error('[LayerWebGLRaster] Shader build failed:', log);
            kernel.current?.handleError('This large layer is displayed in compatibility mode.');
        });
        if (!renderer) {
            canvas.remove();
            onUnavailable();
            return undefined;
        }
        rendererRef.current = renderer;
        // The bitmap box is observed because an ancestor leaving display: none brings the layer back without any event.
        viewportElement.addEventListener(STUDIO_VIEWPORT_TRANSFORM_EVENT, renderer.draw);
        const resizeObserver = new ResizeObserver(renderer.draw);
        resizeObserver.observe(viewportBox);
        resizeObserver.observe(bitmapBox);
        canvas.addEventListener('webglcontextlost', onUnavailable);
        return () => {
            viewportElement.removeEventListener(STUDIO_VIEWPORT_TRANSFORM_EVENT, renderer.draw);
            resizeObserver.disconnect();
            canvas.removeEventListener('webglcontextlost', onUnavailable);
            rendererRef.current = null;
            renderer.dispose();
            canvas.remove();
        };
    }, []);

    const textureKey = imageData ? `${contentVersion}|${imageData.width}x${imageData.height}` : null;

    useLayoutEffect(() => {
        const renderer = rendererRef.current;
        if (!renderer) return;
        // A bitmap of another size resizes the wrapper in this same commit: the previous texture drawn into it would
        // show stretched until the passive upload lands, so that rare change uploads before the paint.
        if (imageData && textureKey && renderer.holdsOtherSize(imageData.width, imageData.height) && !renderer.upload(imageData, textureKey)) {
            onUnavailable();
            return;
        }
        renderer.setHeatmapHue(heatmapHue);
        renderer.draw();
    }, [heatmapHue, imageData, placement, textureKey]);

    // Out of the layout effect, though a store-driven commit (sync lane) still flushes it before the paint. Until the
    // first upload lands the canvas stays hidden.
    useEffect(() => {
        const renderer = rendererRef.current;
        if (!renderer || !imageData || !textureKey) return;
        if (!renderer.upload(imageData, textureKey)) {
            onUnavailable();
            return;
        }
        renderer.draw();
    }, [imageData, textureKey]);

    return <div ref={hostRef} style={HOST_STYLE} />;
};

const LayerWebGLRaster = React.memo(LayerWebGLRasterComponent);
LayerWebGLRaster.displayName = 'LayerWebGLRaster';

export default LayerWebGLRaster;
